import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { getSubArea, getSubAreas, postSubArea, putSubArea } from '../../store/actions/sub_area/sub_area_action';
import { REACT_ROUTES } from '../../constants/react_routes';
import history from '../../services/history';
import TextInput from '../ui_components/text_input';
import { useSnackbar } from 'notistack';
import Autocomplete from '../ui_components/autocomplete';
import { API_PREFIX as API, API_ROUTES } from '../../constants/api_routes';

const useStyles = makeStyles((theme) => ({
	container: {
		padding: theme.spacing(3, 2)
	},
    typography: {
		margin: theme.spacing(3, 0, 2)
    },
    form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
        '& p': {
            color: '#bf1650',
            '&::before': {
                display: 'inline',
                content: '"⚠ "'
            }
        }
	}
}));

const showErrorsAndMessages = (violations, enqueueSnackbar, setError) => {
    if (!violations) {
        enqueueSnackbar('Uspešno ste sačuvali dijagnozu', { variant: 'success' });
        return history.push(REACT_ROUTES.SUB_AREAS);
    }
    for (const violation of violations) {
        enqueueSnackbar(violation?.message, { variant: 'warning' });
        if (violation?.propertyPath) {
            setError(violation?.propertyPath, { message: violation?.message });
        }
    }
}

const SubArea = ({ subAreas, handleClose, initialize }) => {
    const { uuid } = useParams();
    const { container, typography, form } = useStyles();
    const { setValue, control, handleSubmit, reset, setError, formState: { errors } } = useForm();
    const dispatch = useDispatch();

    const { enqueueSnackbar } = useSnackbar();

    const [area, setArea] = useState(null);

    useEffect(() => {
        uuid && (async () => {
            const subArea = await getSubArea(uuid);
            reset(subArea);
            if(subArea.area){
                const areaIri = `${API}${API_ROUTES.AREAS}/${subArea?.area?.uuid}`;
                setArea(areaIri);
                setValue('area', areaIri);
            }
        })();
        !subAreas.collection.length && dispatch(getSubAreas());
    }, [uuid]);

    const onSubmit = async (data) => {
        if (uuid && !handleClose && !initialize) {
            const { violations } = await dispatch(putSubArea(uuid, data));
            return showErrorsAndMessages(violations, enqueueSnackbar, setError);
        }
        const response = await dispatch(postSubArea(data));
        const { violations } = response;
        if (!violations && handleClose && initialize) {
            initialize([response]);
            return handleClose();
        }
        return showErrorsAndMessages(violations, enqueueSnackbar, setError);
    }

    const handleClickBackToList = () => {
        handleClose ? handleClose() : history.push(REACT_ROUTES.SUB_AREAS);
    }

    const handleAreaObject = (object) => {
        setArea(object);
        setValue('area', object);
    }
    return (
        <Container className={container}>
            <Typography align="center" variant="h4" className={typography}>Dijagnoza</Typography>
            <Grid container justify="center" spacing={3}>
                <Grid item>
                    <Button
                        color="primary"
                        size="small"
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        type="button"
                    >
                        Sačuvaj
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleClickBackToList}
                    >
                        Nazad
                    </Button>
                </Grid>
            </Grid>
            <form className={form}>
                <Grid container justify="center" spacing={3}>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="name"
                            label="Naziv dijagnoze"
                            rules={{ required: 'Unesite naziv dijagnoze' }}
                        />
                        <ErrorMessage errors={errors} name="name" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            name="description"
                            control={control}
                            label="Opis"
                            multiline
                            rows={6}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <Autocomplete
                            name="area"
                            label="Oblast"
                            placeholder="Oblast"
                            handleObject={handleAreaObject}
                            api={API_ROUTES.AREAS}
                            preloadValue={area}
                        />
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}

SubArea.propTypes = {
    subAreas: PropTypes.object,
    handleClose: PropTypes.func,
    initialize: PropTypes.func
}

const mapStateToProps = (state) => ({
    subAreas: state.subAreas
});

export default connect(mapStateToProps)(SubArea);
